import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import numeral from 'numeraljs'
import shortid from 'shortid'

import { FormRow, LabelSlot, FieldSlot } from './row'
import ToolTip from './tooltip'
import Button from './button'
import { InputRow, SelectionRow, DateRow } from './campaign-code-generator-rows'
import { GeneratorFields } from '../api-client/api-types'
import {
  validateUrlListAsync,
  removeShortLinkCandidatesGQL,
  addCodesGQL,
  logActionAsync,
} from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import generateCode, {
  countCombinations,
  filterList,
  isFieldAChild,
  getParentValue,
  getChildrenValues,
  makeLinkSecure,
  getValidationStatus,
  isAllFormDataValid,
} from '../helpers/campaign-codes'
import { InnerBox, OuterBox } from './two-columns'
import styles from '../component-styles/campaign-code-generator.module.scss'
import {
  getItemByKeyValue,
  getValidationChecksObject,
  getValidationCheck,
  isValidInput,
  returnUnique,
  isValidUrl,
} from '../helpers'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import ConfirmationButtons from './confirmation-buttons'
import Heading from './heading'
import { logAction } from '../api-client/analytics-client'
// import { addCodes } from '../api-client/graphql-client'
import {
  saveFormData,
  getFormData,
  saveUserData,
  getUserData,
} from '../local-client/local-client'
import Input, { Label } from './input'
import RequestBrandedDomainModal from './request-branded-domain-modal'
import { messages } from '../core/variables'
import CreateQrModal from './create-qr-modal'
import WarningModal from './campaign-code-generator-warning-modal'
import CodeSummary from './campaign-code-summary'
import UrlValidationMessage from './campaign-code-url-validation-message'
import EditableShortLinkInput from './editable-short-link-input'
import CampaignCodeCreatePreviewTable from './campaign-code-create-preview-table'

interface Labels {
  parentID: string
  title: string
  items: string[]
}

interface CampaignCodeGeneratorFormProps {
  single?: boolean
  onGenerateCode: (codes: any[], fullCodes: any) => void
  onRequestNewField?: (value: string) => void
}

export default function CampaignCodeGeneratorForm({
  single = true,
  onGenerateCode,
  onRequestNewField,
}: CampaignCodeGeneratorFormProps): React.ReactElement | null {
  const [shortLinkKey, setShortLinkKey] = useState(shortid.generate())
  const [shortLinkStatus, setShortLinkStatus] = useState<UrlStatus>('')
  const [shortLink, setShortLink] = useState('')
  const [previewTableShortLinks, setPreviewTableShortLinks] = useState({})
  const [showBrandedLinkModal, setShowBrandedLinkModal] = useState(false)
  const [warningModalType, setWarningModalType] = useState<WarningTypes>('')
  const [showWarningModal, setShowWarningModal] = useState(false)
  const savedState = getUserData()
  const initialState = savedState?.linkType || 'short'
  const [linkType, setLinkType] = useState(initialState)
  const dispatch = useDispatch()
  const { generatedStructure, recentlyValidatedUrls } = useSelector(
    (s: any): CampaignCodesState => s.campaignCodes,
  )
  const { currentAccount } = useSelector((s: any): GraphQLState => s.graphql)
  const accountID = currentAccount?.accountID || ''

  const [formKey, setFormKey] = useState(shortid.generate())
  const [url, setUrl] = useState([''])
  const [form, setForm] = useState(generatedStructure)
  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [errors, setErrors] = useState<string[]>([])
  const [submitError, setSubmitError] = useState(false)
  const [combinations, setCombinations] = useState(0)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewCodes, setShowPreviewCodes] = useState<any>(null)
  const [codeCreatedTimeout, setCodeCreatedTimeout] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [currentSingleCode, setCurrentSingleCode] = useState('')
  const [timeoutHandle, setTimeoutHandle] = useState<any>(null)

  const inlineErrorsPresent = errors.length !== 0
  const softDisable =
    inlineErrorsPresent || submitDisabled || shortLinkStatus === 'invalid'

  const delay = 500
  const runValidation = (toValidate) => {
    if (timeoutHandle) {
      window.clearTimeout(timeoutHandle)
    }
    const timeout = window.setTimeout(() => {
      dispatch(validateUrlListAsync({ urlList: [...toValidate] }))
    }, delay)
    setTimeoutHandle(timeout)
  }

  useEffect((): void => {
    const urls = Object.keys(recentlyValidatedUrls)
    const toValidate = url.filter((u) => {
      return urls.indexOf(u) === -1
    })
    if (toValidate.length > 0) {
      runValidation(toValidate)
    }
  }, [recentlyValidatedUrls, url])

  const validationChecks = useMemo(() => {
    return getValidationChecksObject(generatedStructure)
  }, [generatedStructure])

  const validationChecksLanding = useMemo(() => {
    return getValidationChecksObject(generatedStructure, true)
  }, [generatedStructure])

  useEffect(() => {
    const foundShortLink = getItemByKeyValue(
      validationChecks,
      'name',
      'FORCE_SHORT_LINK',
    )
    if (
      foundShortLink === -1 ||
      (foundShortLink !== -1 && foundShortLink.enabled && linkType === 'full')
    ) {
      setLinkType('short')
    }
  }, [generatedStructure])

  const hideLongLinkOption = useMemo(() => {
    const foundShortLink = getItemByKeyValue(
      validationChecks,
      'name',
      'FORCE_SHORT_LINK',
    )

    return foundShortLink === -1 || foundShortLink.enabled
  }, [single, generatedStructure])

  const requireLanding = useMemo(() => {
    return getValidationCheck(validationChecks, 'REQUIRE_LANDING_PAGE')
  }, [validationChecks])

  const landingLengthLimit = useMemo(() => {
    return getValidationCheck(validationChecks, 'LIMIT_URL_LENGTH')
  }, [validationChecks])

  const queryLengthLimit = useMemo(() => {
    return getValidationCheck(validationChecks, 'LIMIT_QUERY_LENGTH')
  }, [validationChecks])

  const showLanding = useMemo(() => {
    return getValidationCheck(validationChecks, 'SHOW_LANDING_PAGE')
  }, [validationChecks])

  const updateForm = (name: string, value: string[], useForm = form): void => {
    if (!useForm) {
      return
    }
    let useUrl = url

    if (name === 'url') {
      setUrl(value)
      useUrl = value
    }

    const currentItem = getItemByKeyValue(useForm.paramDefs, 'fieldName', name)
    const paramDefs = useForm.paramDefs.map((item: any): any => {
      return name === item.fieldName
        ? {
            ...currentItem,
            optionValue: value,
          }
        : item
    })
    const data = {
      ...useForm,
      paramDefs: [...paramDefs],
    }
    const urlValid =
      !requireLanding.enabled ||
      (requireLanding.enabled && useUrl.join('') !== '')
    const disabled = !(urlValid && isAllFormDataValid(data))

    setSubmitDisabled(disabled)

    const countedCombinations = countCombinations(data, useUrl)
    setCombinations(countedCombinations)
    setForm(data)
    saveFormData(useUrl, data, accountID, single)
  }

  useEffect(() => {
    const savedForm = getFormData(generatedStructure, url, accountID, single)
    updateForm('url', savedForm.url, savedForm.form)
    if (savedForm.url.join('').length > 0) {
      dispatch(validateUrlListAsync({ urlList: savedForm.url }))
    }
    setFormKey(shortid.generate())
  }, [single])

  const actionAddSingleCode = (u: null | string[] = null) => {
    setCodeCreatedTimeout(true)
    const useUrl = u || url || ['']
    const codes = generateCode(useUrl, form)
    const useCodesUrl = returnUnique(codes.map((i) => i.url))
    const lP = useCodesUrl.length > 0 ? useCodesUrl[0] : ''
    const codesToAdd = {
      lP,
      cL: codes.map((item) => {
        return {
          tC: item.tC,
          pDfs: item.pDfs.map((i) => [i[0], i[1]]),
          shortLinkID:
            linkType === 'short' || linkType === 'combined' ? shortLink : '',
        }
      }),
    }
    if (linkType === 'short' || linkType === 'combined') {
      dispatch(
        removeShortLinkCandidatesGQL({
          links: [shortLink],
        }),
      )
      setShortLink('')
      window.setTimeout(() => {
        setShortLinkKey(shortid.generate())
      }, 300)
    }

    dispatch(addCodesGQL(codesToAdd))
    const actionData = {
      action:
        linkType === 'short' || linkType === 'combined'
          ? 'add-code-short-link'
          : 'add-code',
      longDescription: 1,
      websiteSection: 'track',
    }
    dispatch(logActionAsync(actionData))
    onGenerateCode(codes, codesToAdd)
    const useShortLink =
      linkType === 'short' || linkType === 'combined' ? shortLink : ''
    const singleCode =
      useShortLink !== '' ? `https://upl.inc/${useShortLink}` : codes[0].code
    setCurrentSingleCode(singleCode)

    if (linkType === 'combined') {
      setShowQrModal(true)
    }

    window.setTimeout(() => {
      setCodeCreatedTimeout(false)
    }, 800)
  }

  const onSubmit = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault()

    if (form === null || codeCreatedTimeout) return

    if (softDisable) {
      setSubmitError(true)
      return
    }
    setSubmitError(false)

    if (single) {
      const link = url[0] || ''
      let isValid = isValidUrl(link)
      const hasAnchor = link.indexOf('#') !== -1
      const useUrl = makeLinkSecure(link)
      const codes = generateCode([useUrl], form)
      const useCodesUrl = returnUnique(codes.map((i) => i.url))

      if (link !== useUrl) {
        updateForm('url', [useUrl])
        isValid = true
      }

      const urlValidLength = isValidInput(
        generateCode([useUrl], form)[0].code,
        [landingLengthLimit],
      )

      const c = generateCode([useUrl], form)[0]
      const query = c.code.replace(c.url, '')
      const queryValidLength = isValidInput(query, [queryLengthLimit])

      setShowPreviewCodes(codes)
      if (!hasAnchor && isValid && urlValidLength && queryValidLength) {
        actionAddSingleCode(useCodesUrl)
      } else {
        setShowWarningModal(true)
        if (hasAnchor) {
          setWarningModalType('has-anchor')
        } else if (!queryValidLength) {
          setWarningModalType('invalid-query-length')
        } else if (!urlValidLength) {
          setWarningModalType('invalid-length')
        } else {
          setWarningModalType('no-url')
        }
      }
    } else {
      const useUrls = url || ['']
      const codes = generateCode(useUrls, form)
      const validUrls = useUrls.filter((u) => isValidUrl(u))
      const isValid = validUrls.length === useUrls.length
      const hasAnchor = JSON.stringify(useUrls).indexOf('#') !== -1

      const c = generateCode(url || [''], form)
      const urlValidLength = isValidInput(
        c.map((item) => item.code),
        [landingLengthLimit],
      )

      // TODO: make this check all codes
      const query = c[0].code.replace(c[0].url, '')
      const queryValidLength = isValidInput(query, [queryLengthLimit])

      setShowPreviewCodes(codes)
      if (!hasAnchor && isValid && urlValidLength && queryValidLength) {
        setShowPreviewModal(true)
      } else {
        setShowWarningModal(true)
        if (hasAnchor) {
          setWarningModalType('has-anchor')
        } else if (!queryValidLength) {
          setWarningModalType('invalid-query-length')
        } else if (!urlValidLength) {
          setWarningModalType('invalid-length')
        } else {
          setWarningModalType('no-url')
        }
      }
    }
  }

  const previewIsDisabled = useMemo(() => {
    const useShortLinks =
      linkType === 'short' || linkType === 'combined'
        ? previewTableShortLinks
        : {}
    const urls = Object.keys(useShortLinks)
    const usePreview =
      previewCodes !== null && (linkType === 'short' || linkType === 'combined')
    return (
      usePreview &&
      (previewCodes.filter((item) => item.selected).length === 0 ||
        urls.length < previewCodes.length)
    )
  }, [previewCodes, previewTableShortLinks, linkType])

  const validationMessage = `No spaces ${
    getValidationCheck(validationChecksLanding, 'NO_SPECIAL_CHARS_LANDING_PAGE')
      .enabled
      ? 'or special characters (?=&)'
      : ''
  } please.`

  useEffect(() => {
    if (showWarningModal) {
      let desc = {}
      if (
        warningModalType === 'invalid-query-length' ||
        warningModalType === 'invalid-length'
      ) {
        const maxLength =
          warningModalType === 'invalid-length'
            ? landingLengthLimit.value
            : queryLengthLimit.value

        const length =
          warningModalType === 'invalid-length'
            ? previewCodes[0].code.length
            : previewCodes[0].code.replace(previewCodes[0].url, '').length
        desc = {
          maxLength,
          length,
        }
      }
      const actionData = {
        action: `warning-modal-${warningModalType}`,
        longDescription: JSON.stringify(desc),
        websiteSection: 'track-create',
      }
      dispatch(logActionAsync(actionData))
    }
  }, [showWarningModal, warningModalType])

  const showShortLinkEdit = useMemo(() => {
    return (linkType === 'short' || linkType === 'combined') && single
  }, [linkType, single])

  const updateErrorObject = (
    key,
    isValid,
    useErrorObject = errors.slice(),
    setErrorValue = true,
  ) => {
    let newError = useErrorObject
    if (isValid) {
      newError = newError.filter((errorKey) => errorKey !== key)
    } else if (key && newError.indexOf(key) === -1) {
      newError.push(key)
    }
    if (setErrorValue) {
      setErrors(newError)
    }
    return newError
  }

  return (
    <>
      <OuterBox className={styles.outerBox} key={formKey}>
        <InnerBox
          className={classNames(styles.innerBox, {
            [styles.singleInnerBox]: single,
          })}
        >
          {!single && (
            <p className={styles.intro}>
              <span>
                Enter or paste multiple values, before approving the code
                combinations.
              </span>
              <Button
                type="clear"
                onClick={() => {
                  updateForm('url', [''], generatedStructure)
                  setFormKey(shortid.generate())
                }}
              >
                Clear all values
              </Button>
            </p>
          )}
          <p className={styles.asterixNote}>* Required fields</p>
          <form onSubmit={onSubmit}>
            {!(
              showLanding !== null &&
              !showLanding.enabled &&
              requireLanding !== null &&
              !requireLanding.enabled
            ) && (
              <>
                <InputRow
                  // defaultError={url.some((u) => {
                  //   return !isValidInput(u, [landingLengthLimit])
                  // })}
                  defaultError={!!(errors.indexOf('landing-page') !== -1)}
                  single={single}
                  className={classNames(
                    styles.rowWrapper,
                    styles.rowWrapperUrl,
                  )}
                  // delay={5000}
                  validation={validationChecksLanding}
                  onValidation={(valid: boolean) => {
                    updateErrorObject('landing-page', valid)
                  }}
                  optional={requireLanding && !requireLanding.enabled}
                  validationMessage={validationMessage}
                  id="landing-page"
                  name={single ? 'Landing page URL' : 'Landing page URL'}
                  updateForm={(value: string[]): void =>
                    updateForm('url', value)
                  }
                  value={url}
                  tooltip="This is the page your user will land on once they click on your campaign link. Make sure your link does not already contain a campaign code."
                  onInputBlur={(currentUrl) => {
                    if (
                      !Object.prototype.hasOwnProperty.call(
                        recentlyValidatedUrls,
                        currentUrl,
                      )
                    ) {
                      runValidation(currentUrl)
                    }
                  }}
                  onSetTagStatus={(currentUrl) => {
                    const urlStatus = getValidationStatus(
                      recentlyValidatedUrls,
                      currentUrl,
                      validationChecks ? [...validationChecks] : [],
                    )
                    if (!urlStatus || urlStatus.length === 0) {
                      // dispatch(validateUrlListAsync({ urlList: [currentUrl] }))
                      return null
                    }
                    // Remove the message so it only shows the status in colour
                    const { status } = urlStatus[0]
                    return { status }
                  }}
                >
                  <UrlValidationMessage
                    url={url}
                    recentlyValidatedUrls={recentlyValidatedUrls}
                    validationChecks={
                      validationChecks ? [...validationChecks] : []
                    }
                  />
                </InputRow>
              </>
            )}
            {form !== null
              ? form.paramDefs.map(
                  (
                    field: GeneratorFields,
                    index: number,
                  ): React.ReactElement => {
                    // const last = form.paramDefs.length === index + 1
                    const last = false
                    const { metaParameter } = field
                    const wrapperClassName = classNames(styles.rowWrapper, {
                      [styles.isMetaParameter]: !!metaParameter,
                    })
                    const tooltipNote = metaParameter
                      ? '**This metadata will only be visible in Uplifter.**'
                      : ''

                    if (field.fieldType === 'select') {
                      const list = filterList(form, field, single)
                      const isChild = isFieldAChild(field)
                      let labelsList: Labels[] = []
                      if (isChild) {
                        const parentValue = getParentValue(form, field)
                        if (parentValue.length > 0) {
                          labelsList = parentValue.map((item) => {
                            const {
                              optionID: parentID,
                              optionName,
                              useLabel,
                            } = item
                            return {
                              parentID,
                              title: useLabel || optionName,
                              items: getChildrenValues(field, item),
                            }
                          })
                        }
                      }
                      const selectValue = field.optionValue || []

                      return (
                        <SelectionRow
                          defaultError={
                            !!(
                              field.fieldID &&
                              errors.indexOf(field.fieldID) !== -1
                            )
                          }
                          single={single}
                          optional={!field.required}
                          isMetaParameter={!!metaParameter}
                          last={last}
                          key={`${field.fieldID}${JSON.stringify(list)}`}
                          // key={`${field.fieldID}`}
                          name={field.fieldName}
                          updateForm={(value: string[]): void => {
                            updateForm(field.fieldName, value)
                          }}
                          list={list}
                          value={selectValue}
                          tooltip={`${field.helpText || ''}${tooltipNote}`}
                          className={wrapperClassName}
                          labels={labelsList}
                          onValidation={(valid: boolean) => {
                            updateErrorObject(field.fieldID, valid)
                          }}
                        >
                          <Button
                            type="text"
                            className={styles.addButton}
                            onClick={(): void => {
                              if (onRequestNewField) {
                                // eslint-disable-next-line
                                // debugger
                                onRequestNewField(field.fieldName)
                              }
                            }}
                          >
                            Request a new {field.fieldName} +
                          </Button>
                        </SelectionRow>
                      )
                    }

                    if (field.fieldType === 'date') {
                      const dateValue =
                        field.optionValue &&
                        field.optionValue.length > 0 &&
                        field.optionValue[0] !== ''
                          ? field.optionValue[0]
                          : null
                      const dateError = !!(
                        field.fieldID && errors.indexOf(field.fieldID) !== -1
                      )
                      return (
                        <DateRow
                          defaultError={dateError}
                          dateFormat={field.dateFormat || 'DD/MM/YYYY'}
                          optional={!field.required}
                          isMetaParameter={!!metaParameter}
                          last={last}
                          key={`${field.fieldID}${dateError}`}
                          name={field.fieldName}
                          updateForm={(value: string[]): void => {
                            // eslint-disable-next-line
                            // debugger
                            updateForm(field.fieldName, value)
                          }}
                          value={dateValue}
                          tooltip={`${field.helpText || ''}${tooltipNote}`}
                          className={wrapperClassName}
                          onValidation={(valid: boolean) => {
                            updateErrorObject(field.fieldID, valid)
                          }}
                        />
                      )
                    }
                    const { copyFromField } = field
                    const copyFromFieldItem = copyFromField
                      ? copyFromField.map((item) => {
                          const found = getItemByKeyValue(
                            form.paramDefs,
                            'fieldID',
                            item,
                          )
                          return found !== -1 ? found.fieldName : ''
                        })
                      : null
                    const isMetaParameter = !!metaParameter
                    // NOTE: hide the field
                    if (copyFromFieldItem && copyFromFieldItem.length > 0) {
                      return <span key={field.fieldID} />
                    }
                    const { forceLowerCase = true } = field
                    const inputValidation = validationChecks
                      ? [...validationChecks]
                      : []
                    const foundConvert = getItemByKeyValue(
                      validationChecks,
                      'name',
                      'CONVERT_LOWER_CASE',
                      true,
                    )
                    if (foundConvert !== -1) {
                      inputValidation[foundConvert] = {
                        enabled: forceLowerCase,
                        name: 'CONVERT_LOWER_CASE',
                      }
                    }
                    if (!forceLowerCase) {
                      const foundAllLowerCase = getItemByKeyValue(
                        validationChecks,
                        'name',
                        'ALL_LOWER_CASE',
                        true,
                      )
                      if (foundAllLowerCase !== -1) {
                        inputValidation[foundAllLowerCase] = {
                          enabled: false,
                          name: 'ALL_LOWER_CASE',
                        }
                      }
                    }
                    const inputRowDefaultError = !!(
                      field.fieldID && errors.indexOf(field.fieldID) !== -1
                    )
                    return (
                      <InputRow
                        defaultError={inputRowDefaultError}
                        single={isMetaParameter ? true : single}
                        validation={inputValidation}
                        onValidation={(valid: boolean) => {
                          updateErrorObject(field.fieldID, valid)
                        }}
                        optional={!field.required}
                        isMetaParameter={isMetaParameter}
                        copyFromLabel={copyFromFieldItem || []}
                        last={last}
                        id={field.fieldID}
                        key={field.fieldID}
                        name={field.fieldName}
                        updateForm={(value: string[]): void =>
                          updateForm(field.fieldName, value)
                        }
                        value={field.optionValue || ['']}
                        tooltip={`${field.helpText || ''}${tooltipNote}`}
                        className={wrapperClassName}
                      />
                    )
                  },
                )
              : null}
            {!hideLongLinkOption && (
              <FormRow
                className={classNames(styles.noPadding, {
                  [styles.rowNoBorder]: linkType === 'full',
                })}
              >
                <LabelSlot className={styles.labelSlot}>
                  <Label id="link-type">Link type</Label>
                  <ToolTip
                    className={styles.tooltip}
                    showOnRight
                    message={messages.linkTypes}
                  />
                </LabelSlot>
                <FieldSlot>
                  <div className={styles.inlineWrap}>
                    <div className={styles.radioPanel}>
                      <Input
                        type="radio"
                        id="link-type-1"
                        name="link-type"
                        label={
                          <>
                            Short link{' '}
                            <span className={styles.recommended}>
                              (recommended)
                            </span>
                          </>
                        }
                        checked={
                          linkType === 'short' || linkType === 'combined'
                        }
                        onClick={(): void => {
                          const val = 'short'
                          setLinkType(val)
                          saveUserData({ linkType: val })
                        }}
                      />
                      <Input
                        type="radio"
                        id="link-type-2"
                        name="link-type"
                        label="Full link"
                        checked={linkType === 'full'}
                        onClick={(): void => {
                          const val = 'full'
                          setLinkType(val)
                          saveUserData({ linkType: val })
                        }}
                      />
                    </div>
                  </div>
                </FieldSlot>
              </FormRow>
            )}
            <FormRow
              className={classNames(styles.noPadding, styles.lastFormRow)}
            >
              <LabelSlot className={styles.labelSlot}>
                {showShortLinkEdit ? (
                  <>
                    <Label id="link-type">Customise link</Label>
                    <ToolTip
                      className={styles.tooltip}
                      showOnRight
                      message={messages.customiseShortLink}
                    />
                  </>
                ) : (
                  <></>
                )}
              </LabelSlot>

              <FieldSlot>
                {showShortLinkEdit && (
                  <div className={styles.linkInputWrapper}>
                    <EditableShortLinkInput
                      defaultKey={shortLinkKey}
                      setShowBrandedLinkModal={setShowBrandedLinkModal}
                      shortLink={shortLink}
                      setShortLink={(sLink) => {
                        setShortLink(sLink)
                        // setShortLinkKey(shortid.generate())
                      }}
                      onChangeStatus={(status) => {
                        setShortLinkStatus(status)
                      }}
                    />
                  </div>
                )}

                {!single && combinations > 1 && (
                  <p className={styles.summaryPar}>
                    {numeral(combinations).format('0,0')} combinations for you
                    to review.
                  </p>
                )}
                <Button
                  align="right"
                  submit
                  onClick={async () => {
                    if (softDisable) {
                      let newErrors = errors.slice()
                      // Check for empty fields
                      if (
                        requireLanding &&
                        requireLanding.enabled &&
                        url.join('') === ''
                      ) {
                        newErrors = updateErrorObject(
                          'landing-page',
                          false,
                          newErrors,
                          false,
                        )
                      } else {
                        newErrors = updateErrorObject(
                          'landing-page',
                          true,
                          newErrors,
                          false,
                        )
                      }
                      if (form !== null && form.paramDefs.length > 0) {
                        form.paramDefs.forEach((item) => {
                          const {
                            fieldID,
                            required,
                            fieldAvailable,
                            optionValue,
                          } = item
                          const valueNotSet =
                            typeof optionValue === 'undefined' ||
                            (Array.isArray(optionValue) &&
                              optionValue.join('') === '')
                          if (required && fieldAvailable && valueNotSet) {
                            newErrors = updateErrorObject(
                              fieldID,
                              false,
                              newErrors,
                              false,
                            )
                          } else {
                            newErrors = updateErrorObject(
                              fieldID,
                              true,
                              newErrors,
                              false,
                            )
                          }
                        })
                      }
                      setErrors(newErrors)

                      const actionData = {
                        action: 'failed-add-code',
                        longDescription: JSON.stringify({
                          ...form,
                          url: [...url],
                        }),
                        websiteSection: 'track-create',
                      }
                      await logAction(actionData)
                    }
                  }}
                  className={classNames(styles.submitButton, {
                    [styles.softDisableButton]:
                      softDisable || codeCreatedTimeout,
                  })}
                  type={
                    softDisable || codeCreatedTimeout ? 'flatPink' : 'container'
                  }
                >
                  {single ? 'Create campaign link' : 'Review links'}
                </Button>
                {submitError && (
                  <p className={styles.footNoteError}>
                    Please have a value for every required parameter.
                  </p>
                )}
              </FieldSlot>
            </FormRow>
          </form>
        </InnerBox>
      </OuterBox>
      {showBrandedLinkModal && (
        <RequestBrandedDomainModal
          onHideModal={() => {
            setShowBrandedLinkModal(false)
          }}
        />
      )}
      {showQrModal && (
        <CreateQrModal
          section="create"
          setShowModal={setShowQrModal}
          useCode={currentSingleCode}
        />
      )}
      {showWarningModal && (
        <WarningModal
          url={url}
          single={single}
          type={warningModalType}
          maxLength={
            warningModalType === 'invalid-length'
              ? landingLengthLimit.value
              : queryLengthLimit.value
          }
          length={
            warningModalType === 'invalid-length'
              ? previewCodes[0].code.length
              : previewCodes[0].code.replace(previewCodes[0].url, '').length
          }
          onYes={() => {
            setShowWarningModal(false)
            if (single) {
              actionAddSingleCode()
            } else {
              setShowPreviewModal(true)
            }
          }}
          onNo={() => {
            setShowWarningModal(false)
          }}
        />
      )}
      {showPreviewModal && previewCodes && previewCodes.length > 0 && (
        <Modal
          active={showPreviewModal}
          toggleActive={() => {
            setShowPreviewModal(false)
          }}
          type="white"
          superWide
        >
          <ModalHead>
            <Heading type={3} align="left">
              Review campaign codes
            </Heading>
          </ModalHead>
          <ModalBody className={styles.modalBody}>
            <p>
              Tick the combinations you need for your campaign and then click
              'Create campaign links'.
            </p>
            <CampaignCodeCreatePreviewTable
              linkType={linkType}
              form={form}
              codes={previewCodes}
              url={url}
              onRefreshShortLinks={(updatedShortLinks) => {
                setPreviewTableShortLinks(updatedShortLinks)
              }}
              onUpdateSelection={(selection) => {
                setShowPreviewCodes(selection)
              }}
            />
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              disabled={previewIsDisabled}
              onYes={() => {
                const useShortLinks =
                  linkType === 'short' || linkType === 'combined'
                    ? previewTableShortLinks
                    : {}
                let countCodes = 0
                const useCodesUrl = returnUnique(previewCodes.map((i) => i.url))
                useCodesUrl.forEach((u: string) => {
                  const codes = previewCodes
                    .map((item) => {
                      if (!item.selected || item.url !== u) {
                        return null
                      }
                      return {
                        tC: item.tC,
                        pDfs: item.pDfs.map((i) => [i[0], i[1]]),
                        shortLinkID:
                          Object.prototype.hasOwnProperty.call(
                            useShortLinks,
                            item.code,
                          ) && useShortLinks[item.code]
                            ? useShortLinks[item.code]
                            : '',
                      }
                    })
                    .filter((item) => item !== null)

                  countCodes += codes.length

                  const codesToAdd = {
                    lP: u || '',
                    cL: codes,
                  }

                  dispatch(addCodesGQL(codesToAdd))
                })

                const actionData = {
                  action:
                    linkType === 'short' || linkType === 'combined'
                      ? 'add-multiple-codes-short-link'
                      : 'add-multiple-codes',
                  longDescription: countCodes,
                  websiteSection: 'track',
                }
                dispatch(logActionAsync(actionData))

                if (linkType === 'short' || linkType === 'combined') {
                  const removeShortLinks = Object.keys(useShortLinks).map(
                    (i) => useShortLinks[i],
                  )
                  dispatch(
                    removeShortLinkCandidatesGQL({
                      links: removeShortLinks,
                    }),
                  )
                  setPreviewTableShortLinks({})
                }
                // dispatch(getCodesByUserGQL())
                const filteredCodes = previewCodes
                  .map((item) => {
                    if (!item.selected) {
                      return null
                    }
                    return item
                  })
                  .filter((item) => item !== null)
                onGenerateCode(filteredCodes, null)
                setShowPreviewModal(false)
              }}
              onNo={(): void => {
                setShowPreviewModal(false)
              }}
              yesLabel="Create campaign links"
              noLabel="Go back"
            >
              <CodeSummary previewCodes={previewCodes} />
            </ConfirmationButtons>
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}
